import { EventEmitter } from 'events';
import Colors from '~/constants/Colors';
import { BattleDebriefDispatcher, InnDispatcher, PlayerDispatcher } from '~/flux/dispatchers';
import queryGetPlayerInnSubmodeState from '~/graphql/inn/queryGetPlayerInnSubmodeState';
import queryGetRandomInnOpponents from '~/graphql/inn/queryGetRandomInnOpponents';
import text from '~/text';
import { awaitSocket, registerDispatchHandlers } from '~/Tools';
import * as handlers from './handlers';

/**
 * These mirror GraphQL payloads that carry more than the store reads, so each keeps an open index
 * signature and names only the fields this module actually touches.
 */
type InnSubmodeStateView = { rank?: number; tournament_index?: number; [key: string]: unknown };
type InnTourneyInstanceView = {
  game_submode?: string;
  players?: Array<{ playerId: string; [key: string]: unknown }>;
  [key: string]: unknown;
};
type InnOpponentView = {
  _id: string;
  player_inn_submode_state?: { rank?: number; tournament_index?: number; [key: string]: unknown };
  [key: string]: unknown;
};

export type InnRankChangeEntry = {
  playerId: string;
  display_name: string | null;
  old_rank: number;
  new_rank: number;
};
export type InnRosterChange = {
  game_submode: string;
  tourneyId: string;
  /** Set when a player rotated out; the resulting one-rank shift for everyone below is implied. */
  departed: { playerId: string; display_name: string | null; rank: number } | null;
  /** Set when players traded places (a battle win); empty for departures. */
  rank_changes: InnRankChangeEntry[];
  your_new_rank: number;
};
export type InnEmbattledNotice = {
  game_submode: string;
  tourneyId: string;
  battle_id: string;
  embattled: Array<{ playerId: string; display_name: string | null; rank: number }>;
};
type InnAttackNotice = {
  game_submode: string;
  attacker_display_name: string | null;
};
type InnDefenseResult = InnAttackNotice & {
  defender_won: boolean;
  peanuts_lost: number;
};
// fetch inn logs when tabbing back in (logs recieved while tabbed out can be out of sync with db)
window.addEventListener('visibilitychange', () => {
  if (!document.hidden && !document.webkitHidden) fetchInnLogs();
});

let playerId;
let _socket;

// the stuff we serve:
let attack_loadout = null;
let current_submode_state: InnSubmodeStateView | null = null;
let current_tourney_instance: InnTourneyInstanceView | null = null;
let inn_logs = {};
let max_inn_tier_reached = 0;
let opponent_to_attack = null;
let opponents: InnOpponentView[] = [];
const player_inn_state = null;

function getAttackLoadout() {
  return attack_loadout;
}
function getOpponentToAttack() {
  return opponent_to_attack;
}

const InnStore = Object.assign({}, EventEmitter.prototype, {
  ATTACK_BATTLE_START_FAILED: 'ATTACK_BATTLE_START_FAILED',
  ATTACK_PREP_STARTED: 'ATTACK_PREP_STARTED',
  ATTACK_SQUAD_UPDATED: 'ATTACK_SQUAD_UPDATED',
  FETCH_INN_LOGS: 'FETCH_INN_LOGS',
  GOT_INN_OPPONENTS: 'GOT_INN_OPPONENTS',
  GOT_INN_LOGS: 'GOT_INN_LOGS',
  GOT_PLAYER_INN_SUBMODE_STATE: 'GOT_PLAYER_INN_SUBMODE_STATE',
  PASSIVE_TOURNEY_RESOLVED: 'PASSIVE_TOURNEY_RESOLVED',
  TOURNEY_ROSTER_CHANGED: 'TOURNEY_ROSTER_CHANGED',
  TOURNEY_EMBATTLED: 'TOURNEY_EMBATTLED',

  getAll() {
    return {
      attack_loadout,
      current_submode_state,
      current_tourney_instance,
      inn_logs,
      max_inn_tier_reached,
      opponent_to_attack,
      opponents,
      player_inn_state,
    };
  },
});
export default InnStore;

awaitSocket(onSocketConnected);
// awaitSocket().then(onSocketConnected);
function onSocketConnected(socket) {
  _socket = socket;
  if (!_socket.has_InnStore_listeners) {
    _socket.on('inn_logs', onInnLogs);
    _socket.on('inn_tourney_instance', onPlayerInnSubmodeState);
    _socket.on('inn_tourney_passive_rank_up', onPassiveRankUp);
    _socket.on('inn_passive_tourney_resolved', onPassiveTourneyResolved);
    _socket.on('inn_tourney_roster_changed', onTourneyRosterChanged);
    _socket.on('inn_tourney_embattled', onTourneyEmbattled);
    _socket.on('inn_attacked', onInnAttacked);
    _socket.on('inn_defense_resolved', onInnDefenseResolved);

    _socket.has_InnStore_listeners = true;
  }
}

BattleDebriefDispatcher.register(
  registerDispatchHandlers({
    [BattleDebriefDispatcher.CLAIM_ALL_REWARDS_INN]: claimAllRewards_inn,
    [BattleDebriefDispatcher.CLAIM_GOLD_REWARD_INN]: claimGoldReward_inn,
    [BattleDebriefDispatcher.CLAIM_ITEM_REWARD_INN]: claimItemReward_inn,
    [BattleDebriefDispatcher.CLAIM_PD_REWARD_INN]: claimPixieDustReward_inn,
    [BattleDebriefDispatcher.DELETE_INN_LOG_ENTRY]: deleteInnLogEntry,
  })
);
PlayerDispatcher.register(
  registerDispatchHandlers({
    [PlayerDispatcher.PLAYER_LOGGED_IN]: onPlayerLoggedIn,
  })
);
InnDispatcher.register(
  registerDispatchHandlers({
    [InnDispatcher.FETCH_INN_LOGS]: fetchInnLogs,
    [InnDispatcher.FETCH_RANDOM_INN_OPPONENTS]: fetchRandomInnOpponents,
    [InnDispatcher.FETCH_PLAYER_INN_SUBMODE_STATE]: fetchPlayerInnSubmodeState,
    [InnDispatcher.START_ATTACK_BATTLE]: async (action) => {
      await handlers.startInnAttackBattle(
        { playerId, getAttackLoadout, getOpponentToAttack, _socket },
        action
      );
    },
    [InnDispatcher.START_ATTACK_PREP]: startAttackPrep,
    [InnDispatcher.SWAP_ATTACK_HERO]: swapAttackHero,
  })
);

function claimAllRewards_inn(action) {
  _socket.emit('claim_all_rewards_inn', {
    playerId,
    inn_log_id: action.inn_log_id,
  });
}

function claimItemReward_inn(action) {
  _socket.emit('claim_item_rewards_inn', {
    playerId,
    inn_log_id: action.inn_log_id,
    itemIds: [action.itemId],
  });
}

function claimGoldReward_inn(action) {
  _socket.emit('claim_gold_reward_inn', {
    playerId,
    inn_log_id: action.inn_log_id,
  });
}

function claimPixieDustReward_inn(action) {
  _socket.emit('claim_pixie_dust_reward_inn', {
    playerId,
    inn_log_id: action.inn_log_id,
  });
}

function deleteInnLogEntry(action) {
  _socket.emit('delete_inn_log_entry', {
    playerId,
    log_id: action.log_id,
  });
}

function onPlayerLoggedIn({ player }) {
  playerId = player._id;
  inn_logs = player.gameState.inn_logs;
  max_inn_tier_reached = player.gameState.max_inn_tier_reached;

  InnStore.emit(InnStore.GOT_INN_LOGS, inn_logs);
}

async function fetchRandomInnOpponents({
  game_submode,
  num_opponents,
}: {
  game_submode: string;
  num_opponents: number;
}) {
  try {
    const result = await queryGetRandomInnOpponents({
      playerId,
      gameSubmode: game_submode,
      numOpponents: num_opponents,
    });

    onRandomInnOpponents({
      game_submode: result.gameSubmode,
      inn_opponents: result.innOpponents,
    });
  } catch (err) {
    console.error('[InnStore] Failed to fetch random inn opponents:', err);
    throw err;
  }
}

async function fetchPlayerInnSubmodeState({ game_submode }) {
  try {
    const result = await queryGetPlayerInnSubmodeState({
      playerId,
      gameSubmode: game_submode,
    });

    onPlayerInnSubmodeState({
      player_inn_submode_state: result.playerInnSubmodeState,
      tourney_instance: result.tourneyInstance,
    });
  } catch (err) {
    console.error('[InnStore] Failed to fetch player inn submode state:', err);
    throw err;
  }
}

function fetchInnLogs() {
  _socket?.emit('get_inn_logs', {
    playerId,
  });
}

function onInnLogs(data) {
  inn_logs = data.inn_logs;
  InnStore.emit(InnStore.GOT_INN_LOGS, inn_logs);
}

function onPassiveRankUp(data) {
  $addMessageLogMessage(
    text('ui.inn.passive_rank_up')(data.game_submode, data.new_rank),
    Colors.BRIGHT_YELLOW
  );
}

function onPassiveTourneyResolved(data) {
  const {
    game_submode,
    new_tourney_change_event_log,
    this_submode_state,
    new_tourney_instance,
    max_tier_reached,
  } = data;
  inn_logs = data.inn_logs;
  max_inn_tier_reached = max_tier_reached;

  const { new_tier } = new_tourney_change_event_log;
  if (current_submode_state === new_tourney_instance.submode_state) {
    current_tourney_instance = new_tourney_instance;
  }

  if (document.hidden || document.webkitHidden) return;

  InnStore.emit(InnStore.GOT_INN_LOGS, inn_logs);
  InnStore.emit(InnStore.PASSIVE_TOURNEY_RESOLVED, data);

  $addMessageLogMessage(text('ui.inn.passive_tourney_resolved')(game_submode), Colors.GREEN);
  const { current_tier } = this_submode_state;
  if (new_tier >= current_tier) {
    $addMessageLogMessage(text('ui.inn.check_for_rewards') + '\n\n', Colors.BRIGHT_YELLOW);
    if (new_tier > current_tier)
      $addMessageLogMessage(text('ui.inn.tier_up_msg')(new_tier), Colors.BRIGHT_YELLOW);
  } else $addMessageLogMessage(text('ui.inn.tier_down_msg')(new_tier), Colors.RED);
}

/**
 * A bracket-mate left or two of them traded places.
 *
 * Deliberately does not patch the cached roster or ranks: re-deriving the server's placement here
 * would be a second implementation of it, and any drift shows up as a challenge rejected for
 * inn_opponent_rank_changed. The mounted inn views refetch authoritative state and own the player-
 * facing messaging, since only they know which opponents are actually on screen.
 */
function onTourneyRosterChanged(data: InnRosterChange) {
  InnStore.emit(InnStore.TOURNEY_ROSTER_CHANGED, data);
}

/** Two bracket-mates entered a battle and can no longer be challenged. Views own the messaging. */
function onTourneyEmbattled(data: InnEmbattledNotice) {
  InnStore.emit(InnStore.TOURNEY_EMBATTLED, data);
}

/**
 * Someone started an inn fight against us. Logged here rather than in a view because inn defenses
 * happen while the player is off doing something else entirely — usually not on the inn screen.
 */
function onInnAttacked(data: InnAttackNotice) {
  // No tabbed-out guard: unlike inn logs there is nothing to resync from on visibilitychange, so
  // skipping the write would discard the notice permanently — in the very case it exists for.
  window.$addMessageLogMessage(
    text('ui.inn.you_were_attacked')(data.attacker_display_name, data.game_submode),
    Colors.RED
  );
}

function onInnDefenseResolved(data: InnDefenseResult) {
  const key = data.defender_won ? 'ui.inn.defense_won' : 'ui.inn.defense_lost';
  window.$addMessageLogMessage(
    text(key)(data.attacker_display_name, data.game_submode, data.peanuts_lost),
    data.defender_won ? Colors.GREEN : Colors.RED
  );
}

function onPlayerInnSubmodeState({ player_inn_submode_state, tourney_instance }) {
  current_submode_state = player_inn_submode_state;
  current_tourney_instance = tourney_instance;
  InnStore.emit(InnStore.GOT_PLAYER_INN_SUBMODE_STATE, {
    player_inn_submode_state,
    tourney_instance,
  });
}

function onRandomInnOpponents({ game_submode, inn_opponents }) {
  opponents = inn_opponents;
  InnStore.emit(InnStore.GOT_INN_OPPONENTS, { game_submode, inn_opponents });
}

function startAttackPrep(action) {
  attack_loadout = JSON.parse(JSON.stringify(action.attack_loadout));
  opponent_to_attack = action.opponent;
  InnStore.emit(InnStore.ATTACK_PREP_STARTED, {
    attack_loadout,
    opponent_to_attack,
  });
}

function swapAttackHero({ hero_handle_in, hero_build_id_to_swap_in, hero_build_id_to_swap_out }) {
  const in_louadout_member_obj = attack_loadout.find(({ handle }) => handle === hero_handle_in);
  const out_loadout_member_obj = attack_loadout.find(
    ({ hero_build_id }) => hero_build_id === hero_build_id_to_swap_out
  );
  const target_slot_index = out_loadout_member_obj.engagement.slot;

  // aassign the hero build ID
  in_louadout_member_obj.hero_build_id = hero_build_id_to_swap_in;
  // update engagement flags & save updated loadout
  const in_loadout_member_slot_buf = in_louadout_member_obj.engagement?.slot;
  in_louadout_member_obj.engagement = { slot: target_slot_index };
  if (out_loadout_member_obj.handle !== in_louadout_member_obj.handle) {
    const must_swap =
      typeof in_loadout_member_slot_buf === 'number' &&
      typeof out_loadout_member_obj.engagement?.slot === 'number';
    out_loadout_member_obj.engagement = must_swap ? { slot: in_loadout_member_slot_buf } : null;
  }

  InnStore.emit(InnStore.ATTACK_SQUAD_UPDATED);
}
